import { api, unwrap } from './client';

/**
 * Reimbursements API client.
 *
 * Backend wraps every response in ApiResponse<T>; always pass through unwrap().
 * Paths begin with /finance/reimbursements.
 *
 * Creator/employee is auto-assigned to the calling user on the backend, so
 * we never send those fields from here.
 */

export async function listReimbursements() {
  const res = await api.get('/finance/reimbursements');
  const d = unwrap(res);
  return Array.isArray(d) ? d : (d?.content || []);
}

export async function listMyReimbursements() {
  const res = await api.get('/finance/reimbursements/my');
  const d = unwrap(res);
  return Array.isArray(d) ? d : (d?.content || []);
}

export async function getReimbursement(id) {
  const res = await api.get(`/finance/reimbursements/${id}`);
  return unwrap(res);
}

/**
 * JSON variant — no file attached on creation.
 *
 * ReimbursementRequest:
 *   { title, description, amount, category, department? }
 */
export async function createReimbursement(payload) {
  const res = await api.post('/finance/reimbursements', payload);
  return unwrap(res);
}

export async function updateReimbursement(id, payload) {
  const res = await api.put(`/finance/reimbursements/${id}`, payload);
  return unwrap(res);
}

/**
 * Multipart variant — backend expects:
 *   - `reimbursement` part: a Blob with type application/json containing the
 *                           JSON-serialised ReimbursementRequest.
 *   - `file`          part: the binary file.
 *
 * Using a typed Blob (rather than a plain string) ensures Spring binds the
 * `reimbursement` request-part as JSON via MappingJackson2HttpMessageConverter.
 */
export async function createReimbursementWithFile(payload, file) {
  const fd = new FormData();
  fd.append(
    'reimbursement',
    new Blob([JSON.stringify(payload)], { type: 'application/json' }),
  );
  if (file) fd.append('file', file);
  const res = await api.post('/finance/reimbursements', fd, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return unwrap(res);
}

export async function submitReimbursement(id) {
  const res = await api.post(`/finance/reimbursements/${id}/submit`);
  return unwrap(res);
}

// --- Paginated and Filtered Endpoints ---

export async function listReimbursementsPaginated(params = { page: 0, size: 10 }) {
  const res = await api.get('/finance/reimbursements/paginated/all', { params });
  return unwrap(res);
}

export async function listMyReimbursementsPaginated(params = { page: 0, size: 10 }) {
  const res = await api.get('/finance/reimbursements/paginated/my', { params });
  return unwrap(res);
}

export async function filterReimbursements(params) {
  const res = await api.get('/finance/reimbursements/paginated/filter', { params });
  return unwrap(res);
}

export async function filterReimbursementsByCategory(category, params = { page: 0, size: 10 }) {
  const res = await api.get(`/finance/reimbursements/paginated/category/${category}`, { params });
  return unwrap(res);
}

export async function filterReimbursementsByStatus(status, params = { page: 0, size: 10 }) {
  const res = await api.get(`/finance/reimbursements/paginated/status/${status}`, { params });
  return unwrap(res);
}

export async function filterReimbursementsByDepartment(department, params = { page: 0, size: 10 }) {
  const res = await api.get(`/finance/reimbursements/paginated/department/${department}`, { params });
  return unwrap(res);
}
